import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError } from "@/lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, RefreshCw, Loader2 } from "lucide-react";

const STEPS = ["Contact", "Service", "VA Status", "Qualifying", "Consent"];
const STORAGE_KEY = "vde_intake_v2";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR","GU","VI","AS","MP"];
const AGE_RANGES = ["18-24","25-34","35-44","45-54","55-64","65+"];
const BRANCHES = ["Army","Navy","Air Force","Marine Corps","Coast Guard","Space Force","National Guard","Reserves"];
const DISCHARGES = ["Honorable","General Under Honorable Conditions","Other Than Honorable","Bad Conduct","Dishonorable","Uncertain"];
const RATINGS = [10,20,30,40,50,60,70,80,90,100];
const GOALS = ["First rating","Increase to a specific percentage","Reach 100%","P&T status","Appeal a denial","Not sure yet"];
const TRIYES = ["Yes","No","Some"];
const TIMEFRAMES = ["Ready now","Within 3 months","Just exploring"];
const VAGOV_SERVICES = ["VA.gov","ID.me","Login.gov"];

function labelForJob(branches) {
  const b = branches || [];
  if (b.includes("Army") || b.includes("Marine Corps")) return "MOS (Military Occupational Specialty)";
  if (b.includes("Navy") || b.includes("Coast Guard")) return "Rating";
  if (b.includes("Air Force") || b.includes("Space Force")) return "AFSC (Air Force Specialty Code)";
  return "Job code / Job title";
}

const empty = {
  first_name: "", last_name: "", email: "", phone: "", state: "", age_range: "",
  branches: [], service_start_year: "", service_end_year: "",
  ship_service: null, vessels: "", job_title: "", discharge_type: "",
  has_rating: null, current_rating: 0, conditions_sc: "",
  has_denied_claim: null, denied_when: "", denied_for: "",
  seen_at_va: null, has_vagov_account: null, vagov_service: "",
  service_med_records: "", private_med_records: "",
  primary_goal: "", currently_working_with: null, currently_working_with_who: "",
  willing_gather: null, timeframe: "", hears_about_us: "", additional_info: "",
  consent: false,
};

function YesNo({ id, value, onChange }) {
  const opts = [{v:true,l:"Yes"},{v:false,l:"No"}];
  return (
    <div className="flex gap-3">
      {opts.map(o => (
        <button
          key={o.l} type="button" onClick={() => onChange(o.v)}
          className={`px-6 h-11 border-2 font-semibold text-sm ${value === o.v ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-primary hover:border-primary"}`}
          data-testid={`${id}-${o.l.toLowerCase()}`}
        >{o.l}</button>
      ))}
    </div>
  );
}

function YesNoSome({ id, value, onChange }) {
  return (
    <div className="flex gap-3 flex-wrap">
      {TRIYES.map(o => (
        <button
          key={o} type="button" onClick={() => onChange(o.toLowerCase())}
          className={`px-6 h-11 border-2 font-semibold text-sm ${value === o.toLowerCase() ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-primary hover:border-primary"}`}
          data-testid={`${id}-${o.toLowerCase()}`}
        >{o}</button>
      ))}
    </div>
  );
}

export default function Intake() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {veteran_id, email}
  const [restored, setRestored] = useState(false);
  const [password, setPassword] = useState("");
  const [creatingAcct, setCreatingAcct] = useState(false);
  const [acctDone, setAcctDone] = useState(false);
  const navigate = useNavigate();
  const skipSave = useRef(false);

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.form) {
          setForm({ ...empty, ...saved.form });
          setStep(saved.step || 0);
          setRestored(true);
        }
      }
    } catch (err) {
      console.warn("Intake draft restore failed:", err);
    }
  }, []);

  // Persist draft
  useEffect(() => {
    if (skipSave.current) return;
    if (done) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ form, step, ts: Date.now() }));
  }, [form, step, done]);

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const toggleBranch = (b) => setForm(s => ({ ...s, branches: s.branches.includes(b) ? s.branches.filter(x => x !== b) : [...s.branches, b] }));

  const clearDraft = () => { skipSave.current = true; localStorage.removeItem(STORAGE_KEY); setForm(empty); setStep(0); setRestored(false); setTimeout(() => skipSave.current = false, 100); };

  const canProceed = () => {
    if (step === 0) return form.first_name && form.last_name && form.email && form.state && form.age_range;
    if (step === 1) return form.branches.length > 0 && form.discharge_type;
    if (step === 2) return form.seen_at_va !== null && form.has_vagov_account !== null && form.service_med_records && form.private_med_records;
    if (step === 3) return form.primary_goal && form.timeframe;
    if (step === 4) return form.consent;
    return true;
  };

  const next = () => {
    if (!canProceed()) return toast.error("Please complete required fields.");
    setStep(s => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep(s => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    if (!form.consent) return toast.error("Please agree to the terms.");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        service_start_year: form.service_start_year ? parseInt(form.service_start_year) : null,
        service_end_year: form.service_end_year ? parseInt(form.service_end_year) : null,
        current_rating: parseInt(form.current_rating) || 0,
        full_name: `${form.first_name} ${form.last_name}`.trim(),
      };
      const { data } = await api.post("/veterans", payload);
      skipSave.current = true;
      localStorage.removeItem(STORAGE_KEY);
      setDone(data);
      toast.success("Intake received. Check your email.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const createAccount = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    setCreatingAcct(true);
    try {
      await api.post(`/veterans/${done.veteran_id}/create-account`, { password });
      setAcctDone(true);
      toast.success("Portal account created. You are logged in.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Account creation failed");
    } finally { setCreatingAcct(false); }
  };

  /* ---------- SUCCESS SCREEN ---------- */
  if (done) {
    const cal = process.env.REACT_APP_CALENDLY_URL || ""; // optional client-side override
    return (
      <div className="mx-auto max-w-4xl px-5 py-14" data-testid="intake-success">
        <div className="text-center">
          <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-6" strokeWidth={1.6} />
          <div className="label-caps mb-3">Intake received</div>
          <h1 className="font-display text-4xl lg:text-5xl">Two things left, and you're set.</h1>
          <p className="mt-4 text-lg text-primary/80 max-w-2xl mx-auto">
            I sent a confirmation to <span className="font-semibold">{done.email}</span>. Please book your free consult and create your portal password below.
          </p>
        </div>

        <div className="mt-12 bg-secondary p-8 border-l-4 border-accent">
          <div className="label-caps mb-2">Step 1 of 2</div>
          <h2 className="font-display text-2xl mb-4">Book your free 20-minute consult</h2>
          {cal ? (
            <iframe title="Book a consult" src={cal} className="w-full h-[700px] border border-border" data-testid="calendly-embed" />
          ) : (
            <div className="p-6 bg-white border-2 border-dashed border-border">
              <p className="text-primary/80">
                Calendly link is not yet configured. Pamela will email you to schedule directly. Set <code>REACT_APP_CALENDLY_URL</code> in the frontend env to embed the scheduler here.
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 bg-white p-8 border-2 border-primary">
          <div className="label-caps mb-2">Step 2 of 2</div>
          <h2 className="font-display text-2xl mb-2">Create your portal password</h2>
          <p className="text-primary/70 mb-5">Your account uses <span className="font-semibold">{done.email}</span>. You'll use the portal to upload documents and follow your progress.</p>
          {acctDone ? (
            <div className="flex items-center gap-3 bg-secondary p-4 border-l-4 border-primary">
              <CheckCircle2 className="w-6 h-6 text-primary" />
              <div>
                <div className="font-semibold">Portal account created.</div>
                <div className="text-sm text-primary/70">You are logged in. The portal is coming online in the next phase.</div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <Input type="password" placeholder="Choose a password (8+ chars)" value={password}
                onChange={(e) => setPassword(e.target.value)} className="h-12 flex-1"
                data-testid="portal-password" />
              <Button onClick={createAccount} disabled={creatingAcct} size="lg" data-testid="portal-create-btn">
                {creatingAcct ? "Creating…" : "Create account"}
              </Button>
            </div>
          )}
        </div>

        <div className="mt-10 text-center">
          <button onClick={() => navigate("/")} className="text-primary underline underline-offset-4 hover:decoration-2" data-testid="intake-home-btn">Back to home →</button>
        </div>
      </div>
    );
  }

  /* ---------- FORM ---------- */
  return (
    <div className="mx-auto max-w-4xl px-5 py-14">
      <div className="label-caps mb-3">Veteran intake</div>
      <h1 className="font-display text-4xl lg:text-5xl">Tell me about your service.</h1>
      <p className="text-primary/70 mt-3 max-w-2xl">Ten to fifteen minutes. Five sections. Your progress is saved automatically. You can close the tab and finish later.</p>

      {restored && (
        <div className="mt-6 flex items-center justify-between bg-secondary border-l-4 border-accent px-5 py-3" data-testid="restored-banner">
          <div className="flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4" /> Resumed your saved draft.
          </div>
          <button onClick={clearDraft} className="text-sm underline underline-offset-4 text-primary/70 hover:text-primary" data-testid="clear-draft">Start over</button>
        </div>
      )}

      {/* Progress bar */}
      <div className="mt-8 grid grid-cols-5 gap-1" data-testid="intake-steps">
        {STEPS.map((s, i) => (<div key={s} className={`h-1.5 ${i <= step ? "bg-primary" : "bg-border"}`}></div>))}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-primary/60">
        <span className="font-semibold">{String(step + 1).padStart(2, "0")} · {STEPS[step]}</span>
        <span>Step {step + 1} of {STEPS.length}</span>
      </div>

      <Card className="mt-8 p-6 lg:p-10 border-2">
        {/* ---------- STEP 0: CONTACT ---------- */}
        {step === 0 && (
          <div className="grid md:grid-cols-2 gap-5">
            <div><Label>First name *</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-2 h-12" data-testid="intake-first-name" /></div>
            <div><Label>Last name *</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-2 h-12" data-testid="intake-last-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 h-12" data-testid="intake-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 h-12" data-testid="intake-phone" /></div>
            <div>
              <Label>State *</Label>
              <Select value={form.state} onValueChange={(v) => set("state", v)}>
                <SelectTrigger className="mt-2 h-12" data-testid="intake-state"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-80">{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Age range *</Label>
              <Select value={form.age_range} onValueChange={(v) => set("age_range", v)}>
                <SelectTrigger className="mt-2 h-12" data-testid="intake-age-range"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{AGE_RANGES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* ---------- STEP 1: SERVICE ---------- */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label>Branch of service (select all that apply) *</Label>
              <p className="text-xs text-primary/60 mt-1">For multiple periods of service, select every branch you served in.</p>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                {BRANCHES.map(b => (
                  <button key={b} type="button" onClick={() => toggleBranch(b)}
                    className={`px-4 h-11 border-2 font-semibold text-sm text-left ${form.branches.includes(b) ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-primary hover:border-primary"}`}
                    data-testid={`branch-${b.toLowerCase().replace(/\s+/g, "-")}`}
                  >{b}</button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label>Entry year</Label>
                <Input type="number" min="1940" max="2030" value={form.service_start_year}
                  onChange={(e) => set("service_start_year", e.target.value)}
                  className="mt-2 h-12" placeholder="e.g. 2004" data-testid="intake-entry-year" />
              </div>
              <div>
                <Label>Separation year</Label>
                <Input type="number" min="1940" max="2030" value={form.service_end_year}
                  onChange={(e) => set("service_end_year", e.target.value)}
                  className="mt-2 h-12" placeholder="e.g. 2012" data-testid="intake-sep-year" />
              </div>
            </div>

            {(form.branches.includes("Navy") || form.branches.includes("Coast Guard")) && (
              <div className="p-5 bg-secondary border-l-4 border-accent">
                <Label>Did you serve aboard a ship or vessel? *</Label>
                <div className="mt-3">
                  <YesNo id="ship-service" value={form.ship_service} onChange={(v) => set("ship_service", v)} />
                </div>
                {form.ship_service && (
                  <div className="mt-4">
                    <Label>Name of the vessel(s)</Label>
                    <Input value={form.vessels} onChange={(e) => set("vessels", e.target.value)} className="mt-2 h-12 bg-white" placeholder="e.g. USS Kitty Hawk (CV-63)" data-testid="intake-vessels" />
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>{labelForJob(form.branches)}</Label>
              <p className="text-xs text-primary/60 mt-1">Enter the code and/or your job title.</p>
              <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className="mt-2 h-12" placeholder="e.g. 11B, Infantryman" data-testid="intake-job" />
            </div>

            <div>
              <Label>Character of discharge *</Label>
              <Select value={form.discharge_type} onValueChange={(v) => set("discharge_type", v)}>
                <SelectTrigger className="mt-2 h-12" data-testid="intake-discharge"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{DISCHARGES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* ---------- STEP 2: VA STATUS ---------- */}
        {step === 2 && (
          <div className="space-y-7">
            <div className="p-5 border-2 border-border">
              <Label>Do you currently have a VA disability rating?</Label>
              <div className="mt-3"><YesNo id="has-rating" value={form.has_rating} onChange={(v) => set("has_rating", v)} /></div>
              {form.has_rating && (
                <div className="mt-5 grid md:grid-cols-2 gap-5">
                  <div>
                    <Label>Combined rating</Label>
                    <Select value={String(form.current_rating || "")} onValueChange={(v) => set("current_rating", parseInt(v))}>
                      <SelectTrigger className="mt-2 h-12 bg-white" data-testid="intake-current-rating"><SelectValue placeholder="Select %" /></SelectTrigger>
                      <SelectContent>{RATINGS.map(r => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Which conditions are service-connected?</Label>
                    <Textarea rows={3} value={form.conditions_sc} onChange={(e) => set("conditions_sc", e.target.value)} className="mt-2 bg-white" data-testid="intake-conditions-sc" />
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-2 border-border">
              <Label>Have you ever filed a VA claim that was denied?</Label>
              <div className="mt-3"><YesNo id="has-denied" value={form.has_denied_claim} onChange={(v) => set("has_denied_claim", v)} /></div>
              {form.has_denied_claim && (
                <div className="mt-5 grid md:grid-cols-2 gap-5">
                  <div><Label>Roughly when?</Label><Input value={form.denied_when} onChange={(e) => set("denied_when", e.target.value)} className="mt-2 h-12 bg-white" placeholder="e.g. 2019" data-testid="intake-denied-when" /></div>
                  <div><Label>For what condition(s)?</Label><Input value={form.denied_for} onChange={(e) => set("denied_for", e.target.value)} className="mt-2 h-12 bg-white" data-testid="intake-denied-for" /></div>
                </div>
              )}
            </div>

            <div>
              <Label>Are you currently being seen at a VA medical center or clinic? *</Label>
              <div className="mt-3"><YesNo id="seen-va" value={form.seen_at_va} onChange={(v) => set("seen_at_va", v)} /></div>
            </div>

            <div>
              <Label>Do you have a VA.gov, ID.me, or Login.gov account? *</Label>
              <div className="mt-3"><YesNo id="has-vagov" value={form.has_vagov_account} onChange={(v) => set("has_vagov_account", v)} /></div>
              {form.has_vagov_account && (
                <div className="mt-4">
                  <Label>Which one?</Label>
                  <Select value={form.vagov_service} onValueChange={(v) => set("vagov_service", v)}>
                    <SelectTrigger className="mt-2 h-12" data-testid="intake-vagov-service"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{VAGOV_SERVICES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <Label>Do you have copies of your service medical records? *</Label>
              <div className="mt-3"><YesNoSome id="stm" value={form.service_med_records} onChange={(v) => set("service_med_records", v)} /></div>
            </div>

            <div>
              <Label>Do you have copies of your private or VA medical records? *</Label>
              <div className="mt-3"><YesNoSome id="pvt" value={form.private_med_records} onChange={(v) => set("private_med_records", v)} /></div>
            </div>
          </div>
        )}

        {/* ---------- STEP 3: QUALIFYING ---------- */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <Label>What is your primary goal? *</Label>
              <div className="mt-3 grid md:grid-cols-2 gap-3">
                {GOALS.map(g => (
                  <button key={g} type="button" onClick={() => set("primary_goal", g)}
                    className={`text-left px-4 h-14 border-2 font-semibold text-sm ${form.primary_goal === g ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-primary hover:border-primary"}`}
                    data-testid={`goal-${g.toLowerCase().slice(0,10).replace(/\s+/g, "-")}`}
                  >{g}</button>
                ))}
              </div>
            </div>

            <div className="p-5 border-2 border-border">
              <Label>Are you currently working with a VSO, attorney, accredited agent, or claims company?</Label>
              <div className="mt-3"><YesNo id="working-with" value={form.currently_working_with} onChange={(v) => set("currently_working_with", v)} /></div>
              {form.currently_working_with && (
                <div className="mt-4">
                  <Label>Which one?</Label>
                  <Input value={form.currently_working_with_who} onChange={(e) => set("currently_working_with_who", e.target.value)} className="mt-2 h-12 bg-white" placeholder="e.g. DAV, an attorney's name, a claims company" data-testid="intake-working-with-who" />
                </div>
              )}
            </div>

            <div>
              <Label>Are you willing to gather your records and write statements with guidance?</Label>
              <div className="mt-3"><YesNo id="willing" value={form.willing_gather} onChange={(v) => set("willing_gather", v)} /></div>
            </div>

            <div>
              <Label>What is your timeframe? *</Label>
              <div className="mt-3 flex flex-wrap gap-3">
                {TIMEFRAMES.map(t => (
                  <button key={t} type="button" onClick={() => set("timeframe", t)}
                    className={`px-5 h-12 border-2 font-semibold text-sm ${form.timeframe === t ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-primary hover:border-primary"}`}
                    data-testid={`timeframe-${t.toLowerCase().slice(0,10).replace(/\s+/g, "-")}`}
                  >{t}</button>
                ))}
              </div>
            </div>

            <div><Label>How did you hear about VDE?</Label><Input value={form.hears_about_us} onChange={(e) => set("hears_about_us", e.target.value)} className="mt-2 h-12" data-testid="intake-hears" /></div>

            <div><Label>Anything else I should know?</Label><Textarea rows={4} value={form.additional_info} onChange={(e) => set("additional_info", e.target.value)} className="mt-2" data-testid="intake-additional" /></div>
          </div>
        )}

        {/* ---------- STEP 4: CONSENT ---------- */}
        {step === 4 && (
          <div className="space-y-6">
            <h2 className="font-display text-2xl">Before you submit</h2>
            <div className="bg-secondary p-6 border-l-4 border-accent text-[15px] leading-relaxed text-primary/85">
              Veterans Disability Education provides education, coaching, and strategic guidance only. Pamela J. Booker is not a VA-accredited claims agent, attorney, or Veterans Service Organization representative, and does not prepare, present, or prosecute VA claims on behalf of veterans. Nothing shared on this site or in a consult is legal or medical advice. Free help filing claims is available from accredited VSOs. Results vary; no outcome is guaranteed.
            </div>
            <label className="flex items-start gap-3 cursor-pointer bg-white p-5 border-2 border-primary" data-testid="consent-label">
              <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-1" data-testid="consent-checkbox" />
              <span className="text-[15px] leading-relaxed">
                I have read and understand the above. I agree to work with VDE as an educational and coaching service. *
              </span>
            </label>
            <div className="text-sm text-primary/70">
              Ready? Click submit, then book your free consult on the next screen.
            </div>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
          <Button type="button" variant="ghost" onClick={back} disabled={step === 0} data-testid="intake-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={next} size="lg" data-testid="intake-next-btn">Next <ArrowRight className="w-4 h-4" /></Button>
          ) : (
            <Button type="button" onClick={submit} disabled={submitting || !form.consent} size="lg" data-testid="intake-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Submit intake <ArrowRight className="w-4 h-4" /></>}
            </Button>
          )}
        </div>
      </Card>

      <p className="mt-6 text-xs text-primary/50 text-center">Your progress is auto-saved on this device. Close the tab and come back anytime.</p>
    </div>
  );
}
